import { useState } from 'react';

interface Review {
  id: number;
  name: string;
  city: string;
  service: string;
  rating: number;
  quote: string;
  initials: string;
  date: string;
}

const reviews: Review[] = [
  {
    id: 1,
    name: 'Jennifer M.',
    city: 'Edmonton, AB',
    service: 'Trim & Door Installation',
    rating: 5,
    quote: 'Absolutely incredible work on our interior trim and two new entry doors. The crew was professional, cleaned up every day, and the finish quality rivals anything I\'ve seen from high-end contractors. Our home looks completely transformed. Highly recommend to any Edmonton homeowner!',
    initials: 'JM',
    date: 'February 2026',
  },
  {
    id: 2,
    name: 'Mark & Sandra T.',
    city: 'Spruce Grove, AB',
    service: 'Hardwood Flooring',
    rating: 5,
    quote: 'We replaced the flooring across our entire main floor — over 1,400 sq ft of engineered hardwood. The installation was flawless, finished ahead of schedule, and the price was very fair. Our house in Spruce Grove has never looked better. We\'ll definitely be calling them again for the basement.',
    initials: 'MS',
    date: 'January 2026',
  },
  {
    id: 3,
    name: 'David R.',
    city: 'Edson, AB',
    service: 'Custom Deck Building',
    rating: 5,
    quote: 'Built us a massive two-tier composite deck that completely changed how we use our backyard. From the initial design meeting through to the final inspection, everything was handled with professionalism. The team drove out to Edson without any hesitation. Worth every penny.',
    initials: 'DR',
    date: 'December 2025',
  },
  {
    id: 4,
    name: 'Patricia L.',
    city: 'Whitecourt, AB',
    service: 'Window Installation',
    rating: 5,
    quote: 'Had seven windows replaced throughout the house before winter hit. The new triple-pane windows made an immediate difference in warmth and sound insulation. The installers were tidy, fast, and very knowledgeable. Serving Whitecourt is a big deal for us — not everyone makes the trip.',
    initials: 'PL',
    date: 'November 2025',
  },
  {
    id: 5,
    name: 'Ryan & Tanya K.',
    city: 'Hinton, AB',
    service: 'Fencing',
    rating: 5,
    quote: 'Hired them to fence our entire property — cedar privacy fence on three sides and a steel gate on the driveway. The work took three days and came out better than we imagined. Strong, straight, and perfectly finished. Great crew — friendly and hard-working from start to finish.',
    initials: 'RK',
    date: 'October 2025',
  },
  {
    id: 6,
    name: 'James O.',
    city: 'Edmonton, AB',
    service: 'Office Cleaning',
    rating: 5,
    quote: 'We have a 4,000 sq ft office in south Edmonton and we\'ve been using their commercial cleaning team every week for six months now. Never missed a day, always thorough, and the staff are respectful of our workspace. I\'ve already referred two other business owners to them.',
    initials: 'JO',
    date: 'March 2026',
  },
];

const StarRating = ({ rating }: { rating: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <i
        key={i}
        className={`ri-star-${i < rating ? 'fill' : 'line'} text-amber-400 text-sm`}
      />
    ))}
  </div>
);

export default function Testimonials() {
  const [featured, setFeatured] = useState<Review>(reviews[0]);

  return (
    <section id="reviews" className="py-24 bg-gradient-to-b from-white to-stone-50">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-amber-600 mb-3">
            Customer Reviews
          </span>
          <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            What Our Clients Say
          </h2>
          <p className="text-slate-500 text-base max-w-xl mx-auto">
            Real feedback from homeowners and businesses across Edmonton, Edson, Hinton, Whitecourt, and Spruce Grove.
          </p>

          {/* Summary bar */}
          <div className="flex items-center justify-center gap-8 mt-8 flex-wrap">
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-slate-900">5.0</span>
              <StarRating rating={5} />
              <span className="text-xs text-slate-400 mt-1">Average Rating</span>
            </div>
            <div className="w-px h-12 bg-stone-200 hidden sm:block" />
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-slate-900">100+</span>
              <span className="text-xs text-slate-400 mt-1">Happy Clients</span>
            </div>
            <div className="w-px h-12 bg-stone-200 hidden sm:block" />
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-slate-900">5+</span>
              <span className="text-xs text-slate-400 mt-1">Cities Served</span>
            </div>
            <div className="w-px h-12 bg-stone-200 hidden sm:block" />
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-slate-900">8+</span>
              <span className="text-xs text-slate-400 mt-1">Years Experience</span>
            </div>
          </div>
        </div>

        {/* Featured review */}
        <div className="bg-slate-900 rounded-2xl p-10 mb-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full translate-x-24 -translate-y-24 pointer-events-none" />
          <div className="relative z-10 flex flex-col md:flex-row gap-8 items-start">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold text-xl">
                {featured.initials}
              </div>
            </div>
            {/* Content */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <StarRating rating={featured.rating} />
                <span className="text-amber-400/70 text-xs">{featured.date}</span>
              </div>
              <blockquote className="text-white text-lg leading-relaxed mb-6 italic">
                &ldquo;{featured.quote}&rdquo;
              </blockquote>
              <div>
                <p className="text-white font-semibold">{featured.name}</p>
                <p className="text-slate-400 text-sm">{featured.city} &mdash; {featured.service}</p>
              </div>
            </div>
          </div>

          {/* Reviewer pill selectors */}
          <div className="flex flex-wrap gap-2 mt-8 border-t border-white/10 pt-6">
            {reviews.map((r) => (
              <button
                key={r.id}
                onClick={() => setFeatured(r)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  featured.id === r.id
                    ? 'bg-amber-500 text-white'
                    : 'bg-white/10 text-slate-300 hover:bg-white/20'
                }`}
              >
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold">
                  {r.initials}
                </span>
                {r.name}
              </button>
            ))}
          </div>
        </div>

        {/* Review cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review) => (
            <button
              key={review.id}
              onClick={() => setFeatured(review)}
              className={`text-left bg-white rounded-xl p-6 border transition-all cursor-pointer ${
                featured.id === review.id
                  ? 'border-amber-400 shadow-md'
                  : 'border-stone-100 hover:border-amber-200 hover:shadow-sm'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {review.initials}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{review.name}</p>
                    <p className="text-slate-400 text-xs">{review.city}</p>
                  </div>
                </div>
                <StarRating rating={review.rating} />
              </div>
              <p className="text-slate-600 text-sm leading-relaxed line-clamp-3">
                &ldquo;{review.quote}&rdquo;
              </p>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-full">
                  {review.service}
                </span>
                <span className="text-xs text-slate-400">{review.date}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Google reviews CTA */}
        <div className="text-center mt-12">
          <p className="text-slate-400 text-sm">
            <i className="ri-google-fill text-amber-500 mr-1" />
            All reviews verified &mdash; see us on Google Maps
          </p>
        </div>

      </div>
    </section>
  );
}
